import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  APP_TYPE,
  ConversationData,
  ConversationPagination,
  ConversationStatusGroup,
  getAppType,
  getUserData,
  getWorkspaceId,
  useAppSelector,
  useLazyGetConversationsQuery,
  useLazyGetConversationStatusesQuery,
  useLazyGetFunnelsQuery,
  useGetFunnelGroupsMutation,
  useLazyGetAllGroupsQuery,
  getAppliedFilters,
  FilterRoute,
  ListFilterKey,
} from '@zonofi/zono-money-store';
import {
  useCustomDateRange,
  useTokenizedPaginatedList,
  useUrlDateRange,
} from '../../hooks';
import { ChipDataItem, ChipFilterSection, FunnelGroupInfo } from '../../types';
import moment from 'moment';
import { getTextFromRange } from '../../helper';
import { isEmpty, isNil, isUndefined } from 'lodash';
import { useParams, useSearchParams } from 'react-router-dom';

export const StatusClassMap: Record<string, string> = {
  [ConversationStatusGroup.OPEN]: 'open',
  [ConversationStatusGroup.INPROGRESS]: 'in-progress',
  [ConversationStatusGroup.CLOSED]: 'closed',
};

const normalize = (s: string) => s.toLowerCase().replace(/[\s_-]/g, '');

export const useGetConversationList = ({
  onDateChipPress,
}: {
  onDateChipPress: () => void;
}) => {
  const { customerId: pathCustomerId } = useParams();
  const [searchParams] = useSearchParams();
  const queryCustomerId = searchParams.get('customerId');
  const customerId = useMemo(
    () => pathCustomerId || queryCustomerId || undefined,
    [pathCustomerId, queryCustomerId]
  );

  const workspaceId = useAppSelector(getWorkspaceId);
  const appState = useAppSelector(getAppType);
  const { appliedDateRange } = useUrlDateRange();
  const { applyNewDateRange } = useCustomDateRange();
  const userData = useAppSelector(getUserData);
  const isFieldOps = appState === APP_TYPE.FIELDS_OPS;
  const appliedFilters = useAppSelector(
    getAppliedFilters(FilterRoute.Conversation)
  );

  const [appliedStatus, setAppliedStatus] = useState<string>('');
  const [conversations, setConversations] = useState<ConversationData[]>([]);
  const [funnelGroupMap, setFunnelGroupMap] = useState<
    Record<string, FunnelGroupInfo>
  >({});
  const [isAutomaticByName, setIsAutomaticByName] = useState<
    Record<string, boolean>
  >({});
  const [paginationData, setPaginationData] =
    useState<ConversationPagination>();
  const [conversationsFunnelId, setConversationsFunnelId] =
    useState<string>('');

  const isAdmin = useMemo(
    () => userData?.workspaceRoles?.includes('adm') ?? false,
    [userData]
  );
  const applyAssignedFilter = useMemo(
    () => appState === APP_TYPE.SELLER_HUB && !isAdmin,
    [isAdmin, appState]
  );

  const [fetchConversations, conversationsResponse] =
    useLazyGetConversationsQuery();
  const [fetchStatuses] = useLazyGetConversationStatusesQuery();
  const [getAllGroups, getAllGroupsResponse] = useLazyGetAllGroupsQuery();
  const [getFunnels] = useLazyGetFunnelsQuery();
  const [getFunnelGroups] = useGetFunnelGroupsMutation();

  const CONVERSATION_FILTERS = useMemo<ChipDataItem[]>(() => {
    return [
      ...(!applyAssignedFilter
        ? [
            { id: '', value: 'All', selected: true },
            ...(!isFieldOps
              ? [
                  {
                    id: 'assigned',
                    value: 'Assigned To Me',
                    selected: false,
                  },
                ]
              : []),
            {
              id: ConversationStatusGroup.OPEN,
              value: 'Open',
              selected: false,
            },
            {
              id: ConversationStatusGroup.INPROGRESS,
              value: 'In Progress',
              selected: false,
            },
            {
              id: ConversationStatusGroup.CLOSED,
              value: 'Closed',
              selected: false,
            },
          ]
        : []),
    ];
  }, [applyAssignedFilter, isFieldOps]);

  const startKey = useMemo(() => {
    if (
      conversationsResponse.data?.pagination?.lastEvaluatedConversationThreadId
    ) {
      return conversationsResponse.data?.pagination
        ?.lastEvaluatedConversationThreadId;
    }
    return undefined;
  }, [conversationsResponse.data]);

  const getConversations = useCallback(
    ({ pageSize, nextToken }: { nextToken?: string; pageSize: number }) => {
      const queryType = appliedFilters?.listFilter?.[ListFilterKey.QueryType];
      const tags = appliedFilters?.listFilter?.[ListFilterKey.ConversationTags];
      const teamMemberIds =
        appliedFilters?.listFilter?.[ListFilterKey.TeamMember];

      fetchConversations({
        sellerWorkspaceId: workspaceId,
        limit: pageSize,
        ...(customerId
          ? { customerId, channelTypes: ['C'], channelSubTypes: ['CUST'] }
          : {}),
        lastEvaluatedTimeToken: nextToken,
        ...(nextToken && !isUndefined(startKey)
          ? { lastEvaluatedConversationThreadId: startKey }
          : {}),
        startDate: moment(appliedDateRange?.startDate, 'DD-MM-YYYY')
          .startOf('day')
          .toISOString(),
        endDate: moment(appliedDateRange?.endDate, 'DD-MM-YYYY')
          .add(1, 'days')
          .startOf('day')
          .toISOString(),
        ...(!isEmpty(tags) ? { tags } : {}),
        ...(!isEmpty(teamMemberIds) ? { teamMemberIds } : {}),
        ...(!isEmpty(queryType) ? { queryType: queryType?.[0] } : {}),
        ...(applyAssignedFilter
          ? {
              assigneeId: userData?.inviteId,
              category: 'Assigned',
            }
          : appliedStatus === ''
            ? ''
            : appliedStatus === 'assigned'
              ? { assigneeId: userData?.inviteId, category: 'Assigned' }
              : { category: appliedStatus }),
      });
    },
    [
      appliedDateRange,
      appliedFilters?.listFilter,
      appliedStatus,
      startKey,
      applyAssignedFilter,
      customerId,
    ]
  );

  useEffect(() => {
    if (conversationsResponse.isSuccess && conversationsResponse.data) {
      const { data = [], pagination } = conversationsResponse.data;
      setConversations(data);
      setPaginationData(pagination);
    }
  }, [conversationsResponse.data]);

  useEffect(() => {
    fetchStatuses({ sellerWorkspaceId: workspaceId });
  }, []);

  const {
    data: conversationsData,
    lastItemRef,
    isPageLoading,
    onScroll,
    refresh,
    isEndOfList,
  } = useTokenizedPaginatedList<ConversationData>({
    fetchDataApiCall: getConversations,
    apiResponseData: conversationsResponse.data?.data || [],
    isLoading:
      conversationsResponse.isFetching || conversationsResponse.isLoading,
    uniqueKey: 'conversationThreadId',
    dependencies: [
      appliedStatus,
      appliedDateRange?.startDate.toString(),
      appliedDateRange?.endDate.toString(),
      customerId ?? '',
      appliedFilters?.listFilter,
    ],
    isError: conversationsResponse.isError,
    nextToken: conversationsResponse.data?.pagination?.lastEvaluatedTimeToken
      ? conversationsResponse.data?.pagination?.lastEvaluatedTimeToken
      : 'stop',
  });

  const updateConversations = (data: Partial<ConversationData>) => {
    setConversations((prev) =>
      prev.map((item) =>
        item.conversationThreadId === data.conversationThreadId
          ? { ...item, ...data }
          : item
      )
    );
  };

  const chipFilters: ChipFilterSection[] = useMemo(
    () => [
      {
        items: [
          {
            id: '',
            value: getTextFromRange(appliedDateRange),
            selected: true,
          },
        ],
        onItemPress: onDateChipPress,
      },
      {
        items: CONVERSATION_FILTERS.map((x) => ({
          ...x,
          selected: appliedStatus === x.id,
        })),
        onItemPress: ({ id }: { id: string }) => setAppliedStatus(id),
      },
    ],
    [appliedStatus, appliedDateRange]
  );

  // Fetch customer groups to get isAutomatic status
  useEffect(() => {
    if (!workspaceId) return;
    getAllGroups({
      sellerWorkspaceId: workspaceId,
      customerGroupTypes: ['CUSTOMER_LIFE_CYCLE'],
    });
  }, [workspaceId, getAllGroups]);

  // Build isAutomaticByName map from customer group response
  useEffect(() => {
    if (!getAllGroupsResponse.data) return;

    const map: Record<string, boolean> = {};
    const normalize = (s: string) => s.toLowerCase().replace(/[\s_-]/g, '');

    getAllGroupsResponse.data.forEach((groupsResp) => {
      // Check rootCustomerGroups for isAutomatic field
      groupsResp.rootCustomerGroups?.forEach((g) => {
        if (g.name) {
          map[normalize(g.name)] = g.isAutomatic;
        }
      });

      // Recursively check folders and their customerGroups
      const processFolders = (folders: any[]) => {
        folders?.forEach((folder) => {
          folder.customerGroups?.forEach((cg: any) => {
            if (cg.name && typeof cg.isAutomatic === 'boolean') {
              map[normalize(cg.name)] = cg.isAutomatic;
            }
          });
          if (folder.subFolders?.length) {
            processFolders(folder.subFolders);
          }
        });
      };

      processFolders(groupsResp.folders);
    });

    setIsAutomaticByName(map);
  }, [getAllGroupsResponse.data]);

  // Fetch all funnel groups once on mount to know icon, group_type, and color per group name
  useEffect(() => {
    if (!workspaceId) return;

    const fetchFunnelGroups = async () => {
      try {
        const funnelsRes = await getFunnels({
          sellerWorkspaceId: workspaceId,
        }).unwrap();
        const funnels = funnelsRes.data?.funnels ?? [];

        if (!funnels.length) return;

        const conversationsFunnel = funnels.find(
          (f) => f.name === 'Conversations'
        );
        if (conversationsFunnel) {
          setConversationsFunnelId(conversationsFunnel.id);
        }

        const groupsResults = await Promise.allSettled(
          funnels.map((funnel) =>
            getFunnelGroups({
              sellerWorkspaceId: workspaceId,
              params: { funnel_id: funnel.id, sellerWorkspaceId: workspaceId },
            }).unwrap()
          )
        );

        const map: Record<string, FunnelGroupInfo> = {};
        groupsResults.forEach((result) => {
          if (result.status === 'fulfilled') {
            (result.value?.data?.groups ?? []).forEach((g: any) => {
              if (g.name) {
                map[normalize(g.name)] = {
                  icon: g.icon ?? '',
                  group_type: g.group_type ?? 'manual', // Will be overridden by isAutomaticByName if available
                  color: g.color ?? '#0067ff',
                };
              }
            });
          }
        });
        setFunnelGroupMap(map);
      } catch {
        // non-critical
      }
    };

    fetchFunnelGroups();
  }, [workspaceId]);

  // Merge funnelGroupMap with isAutomaticByName to use customer-group API as source of truth
  const mergedFunnelGroupMap = useMemo(() => {
    const merged = { ...funnelGroupMap };

    // Override group_type for groups already in the funnel map
    Object.entries(merged).forEach(([key, value]) => {
      if (isAutomaticByName[key] !== undefined) {
        merged[key] = {
          ...value,
          group_type: isAutomaticByName[key] ? 'automatic' : 'manual',
        };
      }
    });

    // Add entries from customer-group API for groups not in the funnel map
    // so automatic groups that weren't in the funnels API are still classified correctly
    Object.entries(isAutomaticByName).forEach(([key, isAutomatic]) => {
      if (!merged[key]) {
        merged[key] = {
          icon: '',
          color: '#595959',
          group_type: isAutomatic ? 'automatic' : 'manual',
        };
      }
    });

    return merged;
  }, [funnelGroupMap, isAutomaticByName]);

  useEffect(() => {
    if (conversationsFunnelId) {
      getFunnelGroups({
        sellerWorkspaceId: workspaceId,
        isConversation: true,
        params: {
          funnel_id: conversationsFunnelId,
          sellerWorkspaceId: workspaceId,
        },
      }).unwrap();
    }
  }, [conversationsFunnelId]);

  return {
    conversationsData,
    onScroll,
    lastItemRef,
    isPageLoading,
    isLoading:
      isNil(paginationData) &&
      (conversationsResponse.isUninitialized ||
        conversationsResponse.isFetching),
    updateConversations,
    chips: chipFilters,
    appliedDateRange,
    applyNewDateRange,
    refetchConversationList: refresh,
    isEndOfList,
    mergedFunnelGroupMap,
  };
};
